import { api, unwrap, type ResponseEnvelope } from '@saas-kit/lib';

export type NotificationStatus =
  | 'pending'
  | 'sent'
  | 'failed'
  | 'read'
  | 'skipped';
export type NotificationChannel = 'in_app' | 'email';

export interface NotificationRecord {
  id: string;
  tenant_id: string;
  user_id: string;
  kind: string;
  channel: NotificationChannel;
  status: NotificationStatus;
  title: string;
  body: string;
  payload: Record<string, unknown>;
  dedupe_key: string | null;
  announcement_id: string | null;
  sent_at: string | null;
  read_at: string | null;
  failed_at: string | null;
  error_message: string | null;
  created_at: string;
}

export interface NotificationPage {
  total: number;
  offset: number;
  limit: number;
}

export interface NotificationList {
  items: NotificationRecord[];
  page: NotificationPage;
}

export interface NotificationPreference {
  kind: string;
  channel: NotificationChannel;
  enabled: boolean;
}

export interface ListNotificationsParams {
  offset?: number;
  limit?: number;
  status?: string;
  channel?: string;
  kind?: string;
}

/** Kinds the backend can emit — drives the inbox filter and preferences grid. */
export const NOTIFICATION_KINDS = [
  'welcome',
  'invitation_issued',
  'invitation_accepted',
  'tenant_suspended',
  'billing_payment_recorded',
  'announcement',
] as const;

export const NOTIFICATION_CHANNELS = ['in_app', 'email'] as const;

export const NOTIFICATION_STATUSES = [
  'pending',
  'sent',
  'failed',
  'read',
  'skipped',
] as const;

export async function listNotifications(
  params: ListNotificationsParams = {},
): Promise<NotificationList> {
  return unwrap<NotificationList>(
    api.get('/notifications', {
      params: {
        offset: params.offset ?? 0,
        limit: params.limit ?? 25,
        ...(params.status ? { status: params.status } : {}),
        ...(params.channel ? { channel: params.channel } : {}),
        ...(params.kind ? { kind: params.kind } : {}),
      },
    }),
  );
}

export async function markNotificationRead(
  notificationId: string,
): Promise<NotificationRecord> {
  return unwrap<NotificationRecord>(
    api.post(`/notifications/${notificationId}/read`),
  );
}

export async function markAllNotificationsRead(): Promise<number> {
  const r = await unwrap<{ updated: number }>(
    api.post<ResponseEnvelope<{ updated: number }>>('/notifications/read-all'),
  );
  return r.updated;
}

export async function listPreferences(): Promise<NotificationPreference[]> {
  return unwrap<NotificationPreference[]>(api.get('/notifications/preferences'));
}

export async function updatePreferences(
  items: NotificationPreference[],
): Promise<NotificationPreference[]> {
  return unwrap<NotificationPreference[]>(
    api.patch('/notifications/preferences', { items }),
  );
}
